import './styles.css';
import { BuyerInput, Priority, Timeline, UseCase, companySizeSeatEstimate, stackLabels, tools, useCaseLabels } from './data/tools';
import { trackEvent } from './lib/analytics';
import { persistLead } from './lib/leads';
import { Recommendation, buyerIntentScore, recommendTools } from './lib/recommender';

const app = document.querySelector<HTMLDivElement>('#app');

if (!app) {
  throw new Error('App root not found');
}

const priorityOptions: Array<{ value: Priority; label: string }> = [
  { value: 'security', label: 'Seguridad y privacidad' },
  { value: 'speed', label: 'Implantación rápida' },
  { value: 'brand', label: 'Marca y contenido' },
  { value: 'automation', label: 'Automatización' },
  { value: 'low-cost', label: 'Coste bajo' },
  { value: 'governance', label: 'Gobierno / SSO / auditoría' },
];

function money(value: number | null): string {
  if (value === null) return 'Precio personalizado';
  return `$${value.toLocaleString('en-US')}/mes estimado`;
}

function renderRecommendation(rec: Recommendation, index: number): string {
  const outboundLabel = rec.tool.monetization.path === 'affiliate' ? 'Abrir partner/proveedor' : 'Abrir fuente oficial';
  return `
    <article class="card recommendation" data-testid="recommendation-card">
      <div class="rank">#${index + 1}</div>
      <div>
        <div class="card-head">
          <div>
            <p class="eyebrow">${rec.tool.category}</p>
            <h3>${rec.tool.name}</h3>
          </div>
          <strong class="score">${rec.score}/100 · ${rec.fitLabel}</strong>
        </div>
        <p>${rec.tool.short}</p>
        <div class="facts">
          <span>${money(rec.estimatedMonthlyUsd)}</span>
          <span>${rec.tool.pricing.display}</span>
          <span>${rec.tool.monetization.estimatedValueEur}</span>
        </div>
        <h4>Por qué aparece</h4>
        <ul>${rec.reasons.map((reason) => `<li>${reason}</li>`).join('')}</ul>
        <h4>Antes de comprar</h4>
        <ul>${rec.warnings.map((warning) => `<li>${warning}</li>`).join('')}</ul>
        <div class="actions">
          <a class="button secondary" href="${rec.tool.monetization.url}" target="_blank" rel="noreferrer" data-outbound="${rec.tool.id}">${outboundLabel}</a>
          <button class="button ghost" data-tool-lead="${rec.tool.id}">${rec.tool.monetization.cta}</button>
        </div>
        <p class="disclosure">${rec.tool.monetization.disclosure}</p>
        <details>
          <summary>Fuentes y fecha de verificación</summary>
          <p>Revisado: ${rec.tool.lastChecked}</p>
          <ul>${rec.tool.sourceUrls.map((url) => `<li><a href="${url}" target="_blank" rel="noreferrer">${url}</a></li>`).join('')}</ul>
        </details>
      </div>
    </article>
  `;
}

function readInput(form: HTMLFormElement): BuyerInput {
  const data = new FormData(form);
  const priorities = data.getAll('priorities') as Priority[];
  return {
    useCase: data.get('useCase') as UseCase,
    companySize: data.get('companySize') as BuyerInput['companySize'],
    stack: data.get('stack') as BuyerInput['stack'],
    budgetMonthlyUsd: Number(data.get('budgetMonthlyUsd') || 500),
    priorities,
    timeline: data.get('timeline') as Timeline,
    email: String(data.get('email') || ''),
    company: String(data.get('company') || ''),
  };
}

function renderResults(input: BuyerInput, recommendations: Recommendation[]): void {
  const resultEl = document.querySelector<HTMLDivElement>('#results');
  if (!resultEl) return;
  const top = recommendations.slice(0, 4);
  resultEl.innerHTML = `
    <div class="result-summary" data-testid="result-summary">
      <p class="eyebrow">Shortlist monetizable</p>
      <h2>Top ${top.length} para ${useCaseLabels[input.useCase]}</h2>
      <p>Estimación con ${companySizeSeatEstimate[input.companySize]} asientos, stack ${stackLabels[input.stack]} y presupuesto de $${input.budgetMonthlyUsd}/mes. Intent score lead: <strong>${buyerIntentScore(input)}/100</strong>.</p>
    </div>
    <div class="recommendations">${top.map(renderRecommendation).join('')}</div>
  `;
  resultEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

function renderToolTable(): string {
  return tools.map((tool) => `
    <tr>
      <td><strong>${tool.name}</strong><br><span>${tool.category}</span></td>
      <td>${tool.useCases.map((item) => useCaseLabels[item]).join('<br>')}</td>
      <td>${tool.pricing.display}</td>
      <td><span class="pill ${tool.monetization.path}">${tool.monetization.path}</span><br>${tool.monetization.status}</td>
    </tr>`).join('');
}

app.innerHTML = `
  <header class="hero">
    <nav class="nav" aria-label="Principal">
      <strong>AI Tools Business Compare</strong>
      <a href="#compare">Comparar</a>
      <a href="#method">Metodología</a>
      <a href="#lead">Lead B2B</a>
    </nav>
    <section class="hero-grid">
      <div>
        <p class="eyebrow">MVP comercial · puerto local 43102</p>
        <h1>Shortlists de herramientas IA que se pueden convertir en afiliación o leads B2B.</h1>
        <p class="hero-copy">No es otro directorio genérico. El comparador prioriza caso de uso, stack, tamaño, presupuesto e intención de compra para recomendar una ruta monetizable: afiliado cuando existe, lead cualificado cuando no.</p>
        <div class="hero-actions">
          <a class="button" href="#compare">Crear shortlist</a>
          <a class="button secondary" href="#economics">Ver unit economics</a>
        </div>
      </div>
      <aside class="metric-card" aria-label="Modelo de monetización">
        <span>Evento principal</span>
        <strong>lead_submit + outbound_click</strong>
        <p>Se trackea localmente para conectar después Plausible/PostHog/CRM sin secretos en Git.</p>
      </aside>
    </section>
  </header>

  <main>
    <section class="section" id="compare">
      <div class="section-title">
        <p class="eyebrow">Comparador guiado</p>
        <h2>Encuentra el encaje por perfil de empresa</h2>
        <p>El formulario fuerza señales comerciales reales: tamaño, presupuesto, stack, prioridad y plazo.</p>
      </div>
      <form class="card form" id="compare-form">
        <label> Caso de uso principal
          <select name="useCase" required>
            ${Object.entries(useCaseLabels).map(([value, label]) => `<option value="${value}">${label}</option>`).join('')}
          </select>
        </label>
        <label> Tamaño de empresa
          <select name="companySize" required>
            <option value="2-10">2-10 empleados</option>
            <option value="11-50" selected>11-50 empleados</option>
            <option value="51-200">51-200 empleados</option>
            <option value="201-1000">201-1000 empleados</option>
          </select>
        </label>
        <label> Stack actual
          <select name="stack" required>
            <option value="mixed">Mixto / por decidir</option>
            <option value="microsoft">Microsoft 365 / Teams</option>
            <option value="google">Google Workspace</option>
          </select>
        </label>
        <label> Presupuesto mensual estimado ($)
          <input type="number" min="50" step="50" name="budgetMonthlyUsd" value="500" required />
        </label>
        <fieldset>
          <legend>Prioridades</legend>
          <div class="checks">
            ${priorityOptions.map((option) => `<label><input type="checkbox" name="priorities" value="${option.value}" /> ${option.label}</label>`).join('')}
          </div>
        </fieldset>
        <label> Plazo de compra
          <select name="timeline" required>
            <option value="now">Este mes</option>
            <option value="quarter" selected>Este trimestre</option>
            <option value="exploring">Explorando</option>
          </select>
        </label>
        <label> Email para recibir shortlist completa
          <input type="email" name="email" placeholder="compras@empresa.com" />
        </label>
        <label> Empresa
          <input type="text" name="company" placeholder="Nombre de empresa" />
        </label>
        <button class="button" type="submit" data-testid="submit-compare">Ver recomendación</button>
      </form>
      <div id="results" class="results" aria-live="polite"></div>
    </section>

    <section class="section economics" id="economics">
      <div class="section-title">
        <p class="eyebrow">Unit economics iniciales</p>
        <h2>Prioridad: intención de compra antes que catálogo infinito</h2>
      </div>
      <div class="grid-3">
        <article class="card"><strong>Afiliación</strong><p>Fireflies, Notion y Jasper tienen señales públicas de programa partner/affiliate. Se priorizan cuando el encaje real no empeora.</p></article>
        <article class="card"><strong>Leads cualificados</strong><p>OpenAI, Microsoft, Google, Anthropic y Zapier monetizan mejor capturando presupuesto, stack y plazo para auditoría/implantación.</p></article>
        <article class="card"><strong>Sponsored placements</strong><p>No hay placements activos. Cuando existan, deben estar separados y etiquetados para no contaminar el scoring.</p></article>
      </div>
    </section>

    <section class="section" id="method">
      <div class="section-title">
        <p class="eyebrow">Datos fuenteables</p>
        <h2>Catálogo estrecho, verificable y actualizable</h2>
        <p>Precios y features cambian; por eso cada ficha tiene fuentes y fecha de revisión.</p>
      </div>
      <div class="table-wrap card">
        <table>
          <thead><tr><th>Herramienta</th><th>Casos</th><th>Precio de referencia</th><th>Monetización</th></tr></thead>
          <tbody>${renderToolTable()}</tbody>
        </table>
      </div>
    </section>

    <section class="section lead" id="lead">
      <div class="card lead-card">
        <p class="eyebrow">Siguiente monetización</p>
        <h2>Convierte comparaciones en oportunidades</h2>
        <p>El MVP guarda leads en localStorage como stub auditable. El siguiente paso natural es conectar CRM/serverless y reemplazar enlaces directos por enlaces afiliados aprobados.</p>
        <pre id="lead-output">Aún no hay lead guardado en esta sesión.</pre>
      </div>
    </section>
  </main>

  <footer>
    <p>Fuentes oficiales enlazadas en cada ficha. Esta web puede recibir compensación por algunas recomendaciones cuando exista programa afiliado aprobado.</p>
  </footer>
`;

const form = document.querySelector<HTMLFormElement>('#compare-form');
const leadOutput = document.querySelector<HTMLPreElement>('#lead-output');
let lastInput: BuyerInput | null = null;
let lastRecommendations: Recommendation[] = [];

form?.addEventListener('submit', (event) => {
  event.preventDefault();
  const input = readInput(form);
  const recommendations = recommendTools(input);
  lastInput = input;
  lastRecommendations = recommendations;
  renderResults(input, recommendations);
  trackEvent('quiz_completed', {
    useCase: input.useCase,
    stack: input.stack,
    companySize: input.companySize,
    budgetMonthlyUsd: input.budgetMonthlyUsd,
    topTool: recommendations[0]?.tool.id,
    buyerIntentScore: buyerIntentScore(input),
  });
  if (input.email && leadOutput) {
    const lead = persistLead(input, recommendations);
    leadOutput.textContent = JSON.stringify(lead, null, 2);
    trackEvent('lead_submit', { intentScore: lead.intentScore, topTool: recommendations[0]?.tool.id, hasCompany: Boolean(input.company) });
  }
});

document.addEventListener('click', (event) => {
  const target = event.target as HTMLElement;
  const outbound = target.closest<HTMLElement>('[data-outbound]');
  if (outbound) {
    trackEvent('outbound_click', { toolId: outbound.dataset.outbound || null });
  }
  const toolLead = target.closest<HTMLElement>('[data-tool-lead]');
  if (toolLead && lastInput) {
    const lead = persistLead(lastInput, lastRecommendations);
    if (leadOutput) leadOutput.textContent = JSON.stringify({ requestedTool: toolLead.dataset.toolLead, ...lead }, null, 2);
    trackEvent('lead_submit', { requestedTool: toolLead.dataset.toolLead || null, intentScore: lead.intentScore });
  }
});

trackEvent('page_view', { catalogSize: tools.length });
